import React, { useState, useMemo, useEffect } from "react";

import moment from "moment";
import GoalMountain from "../../components/gameboard/GoalMountain";
import GoalEditModal from "./GoalEditModal.jsx";
import KpiTreemap from "./KpiTreemap";
import MetricPerformance from "./MetricPerformance";
import Insight from "./Insight";
import LoadingScreen from "../../components/LoadingScreen";

import { UseHolidays, UseCompanyWorkspaces, UseCompanyColors } from "../../hooks/company";
import { UseCompanyKpis, UseTopPlayers, UseGroupKpis } from "../../hooks/kpi";
import { UseIsAdmin, UseUserDetails } from "../../hooks/auth";
import { useCompanyGoal, useCompanyKpiTotal, periodLabel } from "../../hooks/companyGoal";
import { GET_ALL_COMPANY_SCORES } from "../../api/kpi";

import Grid from "@mui/material/Grid";
import SoftBox from "../../components/SoftBox";

import TeamLeaderboard from "./TeamLeaderboard";

const DEFAULT_BRAND = "#1B598E";
function hexToRgb(hex) {
  if (!hex) return { r: 27, g: 89, b: 142 };
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}
function rgba(hex, a) { const { r, g, b } = hexToRgb(hex); return `rgba(${r}, ${g}, ${b}, ${a})`; }
function darken(hex, amt = 0.1) {
  const { r, g, b } = hexToRgb(hex); const f = 1 - amt;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

const CompanyView = () => {
  const { primaryColor: brandPrimary } = UseCompanyColors() || {};
  const themeVars = useMemo(() => {
    const base = brandPrimary || DEFAULT_BRAND;
    return {
      "--gmb-primary": base,
      "--gmb-primary-hover": darken(base, 0.1),
      "--gmb-primary-soft": rgba(base, 0.08),
      "--gmb-primary-tint": rgba(base, 0.14),
      fontFamily: '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };
  }, [brandPrimary]);

  const workspaces = UseCompanyWorkspaces(true);
  const topPlayers = UseTopPlayers("today");
  const isAdmin = UseIsAdmin();
  const { firstName } = UseUserDetails() || {};
  const yetiKPIs = UseGroupKpis();
  const [topList, setTopList] = useState([]);
  const [groupKpis, setGroupKpis] = useState([]);

  // Score calculations is completed here so multiple child components can share it
  const [scoreData, setScoreData] = useState(null);
  const [scores, setScores] = useState(null);
  const [monthlyMatrix, setMonthlyMatrix] = useState(null);
  const [weeklyMatrix, setWeeklyMatrix] = useState(null);
  // Which month the weekly view breaks down (defaults to the current month).
  // weeklyMonth drives which month the weekly heat map breaks down; the month
  // picker that sets it is a pending follow-up, so only the value is read today.
  const [weeklyMonth] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [metricTrend, setMetricTrend] = useState(null);
  const [companyTotal, setCompanyTotal] = useState(null);
  const companyKpis = UseCompanyKpis();
  const today = moment();
  const holidays = UseHolidays(today.format("YYYY-MM"));
  const allHolidays = UseHolidays(); // full list, for per-month working-day math


  useEffect(() => {
    GET_ALL_COMPANY_SCORES().then(({ data }) => {
      if (Object.values(data.scores).length) {
        setScoreData(data.scores);
      }
    });
  }, []);

  useEffect(() => {
    if (!scoreData) return;
    if (workspaces?.length <= 0) return;
    if (!companyKpis || Object.values(companyKpis).length <= 0) return;

    const workspaceList = [...workspaces];
    const scoreSummary = [];

    workspaceList.forEach((workspace) => {
      const currentKpis = [];
      // Acquire specific workspace KPIs
      const { _id, name, parentId } = workspace;
      companyKpis[_id]?.forEach((kpi) => {
        currentKpis.push({
          ...kpi,
          workspace: {
            id: _id,
            name,
            parentId,
          },
        });
      });
      

      // Calculate current yeti days
      const dayCount = moment().daysInMonth();
      let yetiMonth = 0;
      for (let d = 0; d < dayCount; d++) {
        if (holidays.indexOf(d + 1) < 0) {
          const t = moment().startOf("month").add(d, "days");
          // no weekends
          if (t.day() > 0 && t.day() < 6) {
            if (t <= today) {
              yetiMonth += 1;
            }
          }
        }
      }

      const workspaceScores = currentKpis
        .map((kpi) => {
          // Map scores for treemap chart
          const score = scoreData[kpi._id];
          if (!score) return null;

          const target = kpi.target * (kpi.wholeMonth ? 1 : yetiMonth);
          const gap = (score - target) * kpi.direction;
          const ratio = gap / target;

          return {
            score,
            ratio,
            kpiId: kpi._id,
            title: kpi.title,
            workspace: kpi.workspace,
            kind: kpi.kind,
          };
        })
        .filter((kpi) => !!kpi); // Filter KPIs that have scores

      if (workspaceScores.length) {
        scoreSummary.push({
          workspace: workspace?.name,
          workspaceId: workspace?._id,
          image: workspace?.image,
          scores: workspaceScores,
          total:
            workspaceScores.reduce((t, s) => t + (1 + s.ratio), 0) /
            workspaceScores.length,
        });
      }
    });

    // Get average performance across all workspaces with scores
    const average = Math.round(
      (scoreSummary.reduce((t, s) => t + s.total, 0) / scoreSummary.length) *
        100,
    );
    setCompanyTotal(average);
    setScores(scoreSummary);
  }, [companyKpis, workspaces, scoreData, holidays]);

  // ── Metric × month heat-map matrix ──
  // Current month reuses the exact ratios already computed above (so the last
  // column matches the rest of the page). Earlier months are fetched per-month
  // and scored against each month's own to-date target (full working days).
  useEffect(() => {
    if (!scores) return;
    if (workspaces?.length <= 0) return;
    if (!companyKpis || Object.values(companyKpis).length <= 0) return;

    const MONTHS = 6;
    const monthsList = [];
    for (let i = MONTHS - 1; i >= 0; i -= 1) {
      monthsList.push(moment().startOf("month").subtract(i, "months"));
    }
    const labels = monthsList.map((m) => m.format("MMM"));
    const fullLabels = monthsList.map((m) => m.format("MMMM YYYY"));

    // Holiday day-numbers for a specific month (from the full company list),
    // so each month's working-day count uses ITS OWN holidays — same method as
    // the platform's current-month calc, not the current month's calendar.
    const holidayDaysFor = (mStart) => {
      const monthRef = mStart.format("YYYY-MM");
      return (allHolidays || [])
        .filter((item) => item?.date?.startsWith(monthRef))
        .map((item) => moment.utc(item.date).date());
    };

    // Mon–Fri count in a month (minus that month's holidays), optionally capped
    // at today (for the current month, so its target matches `scores`).
    const workingDays = (mStart, cap) => {
      const dim = mStart.daysInMonth();
      const hol = holidayDaysFor(mStart);
      let n = 0;
      for (let d = 0; d < dim; d += 1) {
        const t = moment(mStart).add(d, "days");
        if (t.day() > 0 && t.day() < 6 && hol.indexOf(d + 1) < 0 && (!cap || t <= cap)) {
          n += 1;
        }
      }
      return n;
    };
    // Elapsed working days this month — same basis the current-month ratios use.
    const yetiMonth = workingDays(moment().startOf("month"), today);

    const pastMonths = monthsList.slice(0, MONTHS - 1);
    Promise.all(
      pastMonths.map((m) =>
        GET_ALL_COMPANY_SCORES(null, m.format("YYYY-MM-DD"))
          .then(({ data }) => data?.scores || null)
          .catch(() => null),
      ),
    ).then((pastSets) => {
      const boards = workspaces
        .map((ws) => {
          const kpis = companyKpis[ws._id] || [];
          const wsCurrent = scores.find((s) => s.workspaceId === ws._id);

          const metrics = kpis
            .map((kpi) => {
              const cur = wsCurrent?.scores.find((s) => s.kpiId === kpi._id);

              const cells = monthsList.map((m, idx) => {
                const isCurrent = idx === MONTHS - 1;
                const base = { label: labels[idx], fullLabel: fullLabels[idx], isCurrent };
                // Current month → use processed ratio (matches the page exactly)
                if (isCurrent) {
                  if (!cur) return { ...base, ratio: null };
                  const target = kpi.target * (kpi.wholeMonth ? 1 : yetiMonth);
                  return { ...base, ratio: cur.ratio, value: cur.score, target, kind: kpi.kind };
                }
                const set = pastSets[idx];
                const raw = set ? set[kpi._id] : undefined;
                if (raw === undefined || raw === null) return { ...base, ratio: null };
                const target = kpi.target * (kpi.wholeMonth ? 1 : workingDays(m));
                const ratio = target ? ((raw - target) * kpi.direction) / target : 0;
                return { ...base, ratio, value: raw, target, kind: kpi.kind };
              });

              const hasAny = cells.some((c) => c.ratio !== null && c.ratio !== undefined);
              return hasAny ? { kpiId: kpi._id, title: kpi.title, cells } : null;
            })
            .filter(Boolean);

          return metrics.length
            ? { workspace: ws.name, workspaceId: ws._id, image: ws.image, metrics }
            : null;
        })
        .filter(Boolean);

      setMonthlyMatrix({ labels, subLabels: null, currentIndex: MONTHS - 1, workspaces: boards });
    });
  }, [scores, companyKpis, workspaces, allHolidays]);

  // ── Weekly matrix — the selected month split into 7-day weeks (Week 1 = Jul
  //    1–7, …). Each week's actual is fetched as its OWN date range so the
  //    server aggregates it the SAME way it aggregates a month (sum per person,
  //    then average) — the only way the weekly numbers are trustworthy. Targets
  //    are the per-day KPI target × the working days in that week. ──
  useEffect(() => {
    if (!companyKpis || Object.values(companyKpis).length === 0) return;
    if (!workspaces?.length) return;

    const todayM = moment();
    const mStart = moment(weeklyMonth).startOf("month");
    const mEnd = moment(weeklyMonth).endOf("month");
    const isCurrentMonth = mStart.isSame(todayM, "month");

    // Holiday day-numbers for the selected month, for per-week working-day math.
    const monthRef = mStart.format("YYYY-MM");
    const monthHols = (allHolidays || [])
      .filter((h) => h?.date?.startsWith(monthRef))
      .map((h) => moment.utc(h.date).date());

    // Total working days in the full month — denominator for wholeMonth targets.
    let totalWorkingDays = 0;
    for (let d = moment(mStart); d.isSameOrBefore(mEnd, "day"); d.add(1, "day")) {
      if (d.day() > 0 && d.day() < 6 && !monthHols.includes(d.date())) totalWorkingDays += 1;
    }

    // Fixed 7-day weeks counting from the 1st. Skip weeks that haven't begun.
    const daysInMonth = mEnd.date();
    const weeks = [];
    for (let startDay = 1; startDay <= daysInMonth; startDay += 7) {
      const wStart = moment(mStart).date(startDay);
      if (isCurrentMonth && wStart.isAfter(todayM, "day")) break;
      const endDay = Math.min(startDay + 6, daysInMonth);
      const wEnd = moment(mStart).date(endDay);
      const isCurrent = isCurrentMonth && todayM.date() >= startDay && todayM.date() <= endDay;

      // Working days in this week (capped at today for the in-progress week).
      let weekWorkingDays = 0;
      for (let dd = startDay; dd <= endDay; dd += 1) {
        const dm = moment(mStart).date(dd);
        if (dm.day() === 0 || dm.day() === 6) continue;
        if (monthHols.includes(dd)) continue;
        if (isCurrent && dm.isAfter(todayM, "day")) continue;
        weekWorkingDays += 1;
      }

      weeks.push({
        index: weeks.length,
        from: wStart.format("YYYY-MM-DD"),
        to: wEnd.format("YYYY-MM-DD"),
        label: `Week ${weeks.length + 1}`,
        sub: `${wStart.format("MMM D")}–${wEnd.format("D")}`,
        isCurrent,
        weekWorkingDays,
      });
    }

    const labels = weeks.map((w) => w.label);
    const subLabels = weeks.map((w) => w.sub);
    const currentIndex = weeks.findIndex((w) => w.isCurrent);

    // One score fetch per week (correct server-side aggregation over the range).
    Promise.all(
      weeks.map((w) =>
        GET_ALL_COMPANY_SCORES(null, null, null, w.from, w.to)
          .then(({ data }) => data?.scores || null)
          .catch(() => null),
      ),
    ).then((weekSets) => {
      const boards = workspaces
        .map((ws) => {
          const kpis = companyKpis[ws._id] || [];
          const metrics = kpis
            .map((kpi) => {
              const cells = weeks.map((wk, idx) => {
                const base = {
                  label: wk.label,
                  fullLabel: `${wk.label} · ${wk.sub}`,
                  isCurrent: wk.isCurrent,
                };
                const set = weekSets[idx];
                const raw = set ? set[kpi._id] : undefined;
                if (raw === undefined || raw === null) return { ...base, ratio: null };
                if (wk.weekWorkingDays === 0) return { ...base, ratio: null };

                const target = kpi.wholeMonth
                  ? kpi.target * (wk.weekWorkingDays / Math.max(totalWorkingDays, 1))
                  : kpi.target * wk.weekWorkingDays;
                if (!target) return { ...base, ratio: null };

                const ratio = ((raw - target) * kpi.direction) / target;
                return { ...base, ratio, value: raw, target, kind: kpi.kind };
              });

              const hasAny = cells.some((c) => c.ratio !== null && c.ratio !== undefined);
              return hasAny ? { kpiId: kpi._id, title: kpi.title, cells } : null;
            })
            .filter(Boolean);

          return metrics.length
            ? { workspace: ws.name, workspaceId: ws._id, image: ws.image, metrics }
            : null;
        })
        .filter(Boolean);

      setWeeklyMatrix({ labels, subLabels, currentIndex, workspaces: boards });
    });
  }, [weeklyMonth, companyKpis, workspaces, allHolidays]);

  // ── 12-month metric trend (per workspace) for the Performance-of-metrics
  //    chart. Each month's value is % of that month's target — the same basis
  //    as the heat map — so metrics with wildly different units are comparable
  //    on one axis. Each past month is fetched as its own range (correct
  //    server-side aggregation). ──
  useEffect(() => {
    if (!scores) return;
    if (workspaces?.length <= 0) return;
    if (!companyKpis || Object.values(companyKpis).length <= 0) return;

    const MONTHS = 12;
    const monthsList = [];
    for (let i = MONTHS - 1; i >= 0; i -= 1) {
      monthsList.push(moment().startOf("month").subtract(i, "months"));
    }
    const labels = monthsList.map((m) => m.format("MMM"));

    const holidayDaysFor = (mStart) => {
      const ref = mStart.format("YYYY-MM");
      return (allHolidays || [])
        .filter((item) => item?.date?.startsWith(ref))
        .map((item) => moment.utc(item.date).date());
    };
    const workingDays = (mStart, cap) => {
      const dim = mStart.daysInMonth();
      const hol = holidayDaysFor(mStart);
      let n = 0;
      for (let d = 0; d < dim; d += 1) {
        const t = moment(mStart).add(d, "days");
        if (t.day() > 0 && t.day() < 6 && hol.indexOf(d + 1) < 0 && (!cap || t <= cap)) n += 1;
      }
      return n;
    };
    const pastMonths = monthsList.slice(0, MONTHS - 1);
    Promise.all(
      pastMonths.map((m) =>
        GET_ALL_COMPANY_SCORES(
          null,
          null,
          null,
          moment(m).startOf("month").format("YYYY-MM-DD"),
          moment(m).endOf("month").format("YYYY-MM-DD"),
        )
          .then(({ data }) => data?.scores || null)
          .catch(() => null),
      ),
    ).then((pastSets) => {
      const wsBoards = workspaces
        .map((ws) => {
          const kpis = companyKpis[ws._id] || [];
          const wsCurrent = scores.find((s) => s.workspaceId === ws._id);

          const wsMetrics = kpis
            .map((kpi) => {
              const cur = wsCurrent?.scores.find((s) => s.kpiId === kpi._id);

              // % of target per month (for the "All metrics" overview)…
              const series = monthsList.map((m, idx) => {
                const isCurrent = idx === MONTHS - 1;
                if (isCurrent) {
                  if (!cur) return null;
                  return Math.round((cur.ratio + 1) * 100);
                }
                const set = pastSets[idx];
                const raw = set ? set[kpi._id] : undefined;
                if (raw === undefined || raw === null) return null;
                const target = kpi.target * (kpi.wholeMonth ? 1 : workingDays(m));
                if (!target) return null;
                const ratio = ((raw - target) * kpi.direction) / target;
                return Math.round((ratio + 1) * 100);
              });

              // …and the raw value per month (for a single metric's focus view).
              const real = monthsList.map((m, idx) => {
                const isCurrent = idx === MONTHS - 1;
                if (isCurrent) return cur ? cur.score : null;
                const set = pastSets[idx];
                const raw = set ? set[kpi._id] : undefined;
                return raw === undefined || raw === null ? null : raw;
              });

              if (!series.some((v) => v !== null)) return null;

              const total = cur?.score ?? 0;
              const prevRaw = pastSets[MONTHS - 2] ? pastSets[MONTHS - 2][kpi._id] : undefined;
              const deltaPct =
                prevRaw !== undefined && prevRaw !== null && prevRaw !== 0
                  ? ((total - prevRaw) / Math.abs(prevRaw)) * 100
                  : null;

              return {
                kpiId: kpi._id,
                title: kpi.title,
                kind: kpi.kind,
                total,
                deltaPct,
                series,
                real,
              };
            })
            .filter(Boolean);

          return wsMetrics.length
            ? { workspace: ws.name, workspaceId: ws._id, metrics: wsMetrics }
            : null;
        })
        .filter(Boolean);

      setMetricTrend({ months: labels, workspaces: wsBoards });
    });
  }, [scores, companyKpis, workspaces, allHolidays]);

  useEffect(() => {
    if (!scores) return;
    if (!topPlayers) return;
    if (!yetiKPIs) return;
    if (groupKpis.length > 0) return;

    const yetiPlayer = topPlayers.find(player => player.isYeti)
    if (!yetiPlayer) return;

    // Yeti score is always 100 (%)
    yetiPlayer.scores.total = 100;

    // Shape data for Leaderboard use using KPI ratio normalized to 100 (%)
    const companyTopList = [yetiPlayer, ...scores
      .filter(item => item.workspaceId !== undefined)
      .map((item) => {
        const leaderboardItem = {
          userId: item.workspaceId,
          scores: {
            total: item?.total * 100
          },
          image: item.image,
          firstName: item.workspace,
          lastName: ""
        }

        item.scores.forEach((kpiScore) => {
          leaderboardItem.scores[kpiScore.kpiId] = {
            amt: kpiScore.score,
            pts: (kpiScore.ratio + 1) * 100
          }
        })

        return leaderboardItem
    })]

    // Workspace and Yeti KPI info
    let companyGroupKpis = [];

    if (scores.length > 0) {
      companyGroupKpis = scores[0].scores.map(item => ({
        _id: item.kpiId,
        title: item.title,
        kind: item.kind
      }))
    }

    Object.keys(yetiPlayer.scores).forEach(key => {
      const yetiKpi = yetiKPIs.find(item => item._id === key)

      if (yetiKpi &&
        (yetiPlayer.scores[key].amt !== 0) &&
        (companyGroupKpis.findIndex(kpi => kpi._id === key) === -1)
      ) {
        companyGroupKpis.push({
          _id: key,
          title: yetiKpi.title,
          kind: yetiKpi.kind
        })
      }
    })

    setTopList(companyTopList.sort((a, b) => (b.scores.total - a.scores.total)))
    setGroupKpis(companyGroupKpis)
  }, [scores, topPlayers, groupKpis, yetiKPIs])

  /* Company Goal — drives the GoalMountain visual + admin edit modal */
  const [goal, setGoal] = useCompanyGoal();
  const goalCurrent = useCompanyKpiTotal(goal?.kpiId, goal?.period || "month");
  const goalKpiTitle = useMemo(() => {
    if (!goal?.kpiId || !yetiKPIs) return "";
    return yetiKPIs.find((k) => k._id === goal.kpiId)?.title || "";
  }, [goal?.kpiId, yetiKPIs]);
  const [goalModalOpen, setGoalModalOpen] = useState(false);

  return (
    <div className="w-full" style={themeVars}>
      {/* Welcome greeting — replaces the old image banner. Date + name pull
          live from moment() and the signed-in user. */}
      <SoftBox
        sx={{
          paddingX: { mini: "1rem", md: "2rem" },
          pt: { mini: "1.25rem", md: "1.75rem" },
          pb: { mini: "1rem", md: "1.25rem" },
        }}
      >
        <div style={{ fontFamily: "DM Sans", fontSize: "12px", fontWeight: 500, color: "#868C98", lineHeight: 1.4 }}>
          {today.format("dddd, MMM DD, YYYY")}
        </div>
        <div style={{ fontFamily: "DM Sans", fontSize: "24px", fontWeight: 600, letterSpacing: "-0.01em", color: "#0A0D14", lineHeight: 1.2, marginTop: "6px" }}>
          Welcome back{firstName ? `, ${firstName}` : ""}
        </div>
      </SoftBox>

      {/* Row 1 — Performance of metrics + Insight (moved above the heat map) */}
      <Grid
        container
        spacing={3}
        sx={{
          paddingX: { mini: "1rem", md: "2rem" },
        }}
      >
        <Grid item mini={12} lg={8}>
          <MetricPerformance trend={metricTrend} />
        </Grid>
        <Grid item mini={12} lg={4}>
          <Insight
            topList={topList}
            metricTrend={metricTrend}
            companyKpis={companyKpis}
          />
        </Grid>
      </Grid>

      {/* Row 2 — Goal of the mountain + Metric performance heat map */}
      <Grid
        container
        spacing={3}
        sx={{
          mt: 0.5,
          paddingX: { mini: "1rem", md: "2rem" },
        }}
      >
        <Grid item mini={12} lg={5}>
          <SoftBox
            borderRadius="lg"
            sx={{
              height: { xs: "360px", md: "400px", lg: "430px" },
              background: "#ffffff",
              border: "1px solid #EAECF0",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              p: "16px 20px 6px",
            }}
          >
            {/* Card header — inside the container */}
            <div className="flex justify-between items-center" style={{ flexShrink: 0 }}>
              <span style={{ fontFamily: "DM Sans", fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em", color: "#0F172A", lineHeight: 1.2 }}>
                Goal of the mountain
              </span>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setGoalModalOpen(true)}
                  style={{
                    fontFamily: "DM Sans",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    padding: "0 14px",
                    height: 32,
                    borderRadius: 10,
                    border: "1px solid var(--gmb-primary-tint, rgba(27,89,142,0.20))",
                    background: "var(--gmb-primary-soft, rgba(27,89,142,0.08))",
                    color: "var(--gmb-primary, #1B598E)",
                    cursor: "pointer",
                    transition: "background 150ms ease",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "var(--gmb-primary-tint, rgba(27,89,142,0.14))"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "var(--gmb-primary-soft, rgba(27,89,142,0.08))"; }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                    {goal ? (
                      <><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></>
                    ) : (
                      <path d="M12 5v14M5 12h14" />
                    )}
                  </svg>
                  {goal ? "Edit goal" : "Set goal"}
                </button>
              )}
            </div>

            <SoftBox sx={{ flex: 1, minHeight: 0, position: "relative", mt: "6px" }}>
              {companyTotal !== null ? (
                <GoalMountain
                  darkMode={false}
                  noScroll
                  goalTarget={goal?.target || 0}
                  goalCurrent={goalCurrent}
                  goalKpiTitle={goalKpiTitle}
                  goalPeriodLabel={periodLabel(goal?.period)}
                />
              ) : (
                <div className="h-full w-full flex justify-center items-center">
                  <LoadingScreen height="200px" logoHeight="200px" />
                </div>
              )}
            </SoftBox>
          </SoftBox>
          {goalModalOpen && (
            <GoalEditModal
              open={goalModalOpen}
              onClose={() => setGoalModalOpen(false)}
              goal={goal}
              setGoal={setGoal}
              kpis={yetiKPIs || []}
            />
          )}
        </Grid>
        <Grid item mini={12} lg={7} className="h-full">
          <SoftBox sx={{ minHeight: { xs: "360px", md: "400px", lg: "430px" } }}>
            <KpiTreemap matrix={monthlyMatrix} weeklyMatrix={weeklyMatrix} />
          </SoftBox>
        </Grid>
      </Grid>

      {/* Row 3 — Team leaderboard. Its own container with the same mt as the
          rows above so the Row 1→2 and Row 2→3 gaps are identical. */}
      <Grid
        container
        spacing={3}
        sx={{ mt: 0.5, mb: 6, paddingX: { mini: "1rem", md: "2rem" } }}
      >
        <Grid item mini={12}>
          <TeamLeaderboard topList={topList} allKpis={companyKpis} />
        </Grid>
      </Grid>
    </div>
  );
};

export default CompanyView;
